/*
 * consent/catalog.ts — fonte única do que o portal guarda no navegador.
 *
 * O painel de preferências e a seção 11 da Política de Privacidade leem esta
 * mesma lista; um item novo de armazenamento entra aqui, ou não existe para a
 * pessoa. É o catálogo da plataforma Shiba reduzido ao portal: a sessão da área
 * de membros (o SDK do Cognito só guarda em memória; quem persiste é
 * lib/auth/session), o tema e o registro da escolha.
 * As preferências que a pessoa mesma define são NECESSÁRIAS: guardam uma ação
 * explícita dela, a isenção que ANPD e CNIL reconhecem.
 */
import { SITE_NAME } from '@/lib/seo';
import { CONSENT_COOKIE, COOKIE_NOTICE_KEY, type ConsentCategory } from './consent';

export type StorageKind = 'cookie' | 'localStorage' | 'sessionStorage';

export interface StorageItem {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
  kind: StorageKind;
}

export interface ConsentCategoryInfo {
  id: ConsentCategory;
  title: string;
  description: string;
  /** Necessários: sempre ativos, sem interruptor. */
  required: boolean;
  items: readonly StorageItem[];
}

export const STORAGE_KIND_LABEL: Record<StorageKind, string> = {
  cookie: 'Cookie',
  localStorage: 'Armazenamento local',
  sessionStorage: 'Armazenamento da sessão',
};

const FIRST_PARTY = SITE_NAME;

export const CONSENT_CATALOG: readonly ConsentCategoryInfo[] = [
  {
    id: 'necessary',
    title: 'Necessários',
    description:
      'Mantêm a sessão, a segurança e as preferências que você mesmo escolhe. Sem eles o site não funciona, por isso ficam sempre ativos.',
    required: true,
    items: [
      {
        name: CONSENT_COOKIE,
        provider: FIRST_PARTY,
        purpose: 'Guarda a sua escolha sobre cookies.',
        duration: '12 meses',
        kind: 'cookie',
      },
      {
        // SESSION_KEY de lib/auth/session.ts: o ID token e o refresh da área de membros.
        name: 'seesforce.blogSession',
        provider: FIRST_PARTY,
        purpose: 'Mantém a sua sessão na área de membros depois de entrar.',
        duration: 'Até fechar a aba',
        kind: 'sessionStorage',
      },
      {
        // PENDING_KEY de lib/auth/federated.ts: a transação PKCE entre o clique e a volta.
        name: 'seesforce.blogAuthPending',
        provider: FIRST_PARTY,
        purpose: 'Conclui com segurança um login iniciado com o Google.',
        duration: 'Até concluir o login',
        kind: 'sessionStorage',
      },
      {
        name: 'habilita.theme',
        provider: FIRST_PARTY,
        purpose: 'Lembra o tema escolhido (claro, escuro ou do sistema).',
        duration: 'Até você mudar',
        kind: 'localStorage',
      },
      {
        name: COOKIE_NOTICE_KEY,
        provider: FIRST_PARTY,
        purpose: 'Lembra que você já leu o aviso de cookies.',
        duration: 'Até você limpar os dados do site',
        kind: 'localStorage',
      },
    ],
  },
  {
    id: 'advertising',
    title: 'Publicidade',
    description:
      'Permitem que o Google AdSense exiba anúncios no blog, meça o desempenho deles e os personalize. Só são usados com o seu consentimento.',
    required: false,
    items: [
      {
        name: '__gads, __gpi, __eoi',
        provider: 'Google (terceiro)',
        purpose: 'Exibir anúncios, limitar a repetição e medir o desempenho.',
        duration: 'Até 13 meses',
        kind: 'cookie',
      },
      {
        name: 'IDE, test_cookie (doubleclick.net)',
        provider: 'Google (terceiro)',
        purpose: 'Personalizar anúncios e verificar se o navegador aceita cookies.',
        duration: 'Até 13 meses',
        kind: 'cookie',
      },
    ],
  },
];
